import React, { memo, useEffect, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useIntl } from '@atlassian/jira-intl';
import { QueryError } from '@atlassian/jira-business-error-handling/src/utils/query-error/index.tsx';
import { ExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { VIEW_JIRA_HORIZONTAL_NAVIGATION_EXPERIENCE } from '@atlassian/jira-horizontal-nav-common/src/constants.tsx';
import { CONTAINER } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-layer.tsx';
import { useHandleError } from '@atlassian/jira-horizontal-nav-common/src/services/handle-error/useHandleError.tsx';
import { useShortcutsData } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/shortcuts/shortcuts-data/index.tsx';
import { ShortcutsContainer } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/shortcuts/state-container/index.tsx';
import { PROJECT } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/container-type.tsx';
import { TAB } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import type { TabsContent_project$key } from '@atlassian/jira-relay/src/__generated__/TabsContent_project.graphql';
import type { TabsContent_query$key } from '@atlassian/jira-relay/src/__generated__/TabsContent_query.graphql';
import { ContainerNavigationTabs } from '@atlassian/jira-horizontal-nav-tabs/src/ui/ContainerNavigationTabs.tsx';
import { JIRA_TABS } from '../../constants.tsx';
import messages from './messages.tsx';

type Props = {
	queryFragment: TabsContent_query$key;
	projectFragment?: TabsContent_project$key | null;
};

const TabsContent = ({ queryFragment, projectFragment = null }: Props) => {
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment TabsContent_query on Query {
				jira @required(action: THROW) {
					containerNavigation(input: $containerNavigationInput)
						@required(action: THROW)
						@optIn(to: "JiraContainerNavigation") {
						...ContainerNavigationTabs_container
						... on JiraContainerNavigation {
							navigationItems(first: $first) {
								edges {
									__typename
								}
								errors {
									# eslint-disable-next-line @atlassian/relay/unused-fields
									message
									# eslint-disable-next-line @atlassian/relay/unused-fields
									extensions {
										statusCode
									}
								}
							}
						}
						__typename
						... on QueryError {
							# eslint-disable-next-line @atlassian/relay/unused-fields
							message
							# eslint-disable-next-line @atlassian/relay/unused-fields
							extensions {
								statusCode
							}
						}
					}
				}
			}
		`,
		queryFragment,
	);

	const projectData = useFragment(
		graphql`
			fragment TabsContent_project on JiraProject {
				projectType
				projectId
			}
		`,
		projectFragment,
	);

	const { handleError } = useHandleError();
	const { data: shortcutsData, loading } = useShortcutsData();

	const containerNavigation =
		data.jira.containerNavigation?.__typename === 'JiraContainerNavigation'
			? data.jira.containerNavigation
			: null;
	const navigationItems = containerNavigation ? containerNavigation?.navigationItems : null;

	useEffect(() => {
		const containerNavigationQueryError =
			data.jira.containerNavigation?.__typename === 'QueryError'
				? data.jira.containerNavigation
				: null;

		if (navigationItems?.errors && navigationItems?.errors?.length > 0) {
			const error = navigationItems.errors[0];
			handleError(new QueryError(error));
		} else if (containerNavigationQueryError) {
			handleError(new QueryError(containerNavigationQueryError));
		} else if (navigationItems?.edges == null) {
			handleError(new Error('navigationItems.edges failed to load'));
		}
	}, [
		containerNavigation?.navigationItems,
		data,
		handleError,
		navigationItems?.edges,
		navigationItems?.errors,
	]);

	const attributes = useMemo(
		() => ({
			projectType: (projectData?.projectType ?? '').toLowerCase(),
			navigationContainer: TAB,
			navigationLayer: CONTAINER,
			withContainerNav: true,
		}),
		[projectData?.projectType],
	);

	return (
		containerNavigation &&
		navigationItems &&
		!navigationItems.errors &&
		navigationItems?.edges && (
			<ContextualAnalyticsData
				sourceType={SCREEN}
				sourceName="horizontalNavJiraTabsContent"
				containerType={PROJECT}
				containerId={String(projectData?.projectId ?? '')}
				attributes={attributes}
			>
				<ShortcutsContainer data={shortcutsData} loading={loading} isGlobal>
					<ContainerNavigationTabs
						containerFragment={data.jira.containerNavigation}
						tabsDescriptor={JIRA_TABS}
						label={formatMessage(messages.projectNavigation)}
					/>

					<ExperienceSuccess
						experience={VIEW_JIRA_HORIZONTAL_NAVIGATION_EXPERIENCE.experience}
						attributes={{
							withContainerNav: true,
						}}
					/>
				</ShortcutsContainer>
				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		)
	);
};

export default memo(TabsContent);
